import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import TopicClickSuggestion from "./click-suggestion-topic";
import LoadingIndicator from "@default/general-components/loading-indicator";
import Link from "@default/general-components/link/link";
import { useLocation } from "react-router-dom";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import SwiperSimpleNavigation from "@default/paragraphs/media-gallery/swiper-simple-navigation";

const ClickSuggestionsTopicsRbxCompany = ({ content }) => {
  const { simpleDesign } = useAccessibilityContext();

  const [swiperComponents, setSwiperComponents] = useState({
    Swiper: null,
    SwiperSlide: null,
  });

  const [isCssLoaded, setIsCssLoaded] = useState(false);

  const location = useLocation();

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const loadSwiper = async () => {
      try {
        const { Swiper, SwiperSlide } = await import("swiper/react");
        const { Navigation } = await import("swiper/modules");
        await import("swiper/css");

        setSwiperComponents({ Swiper, SwiperSlide, Navigation });
        setIsCssLoaded(true);
      } catch (err) {
        console.error("Error importing Swiper, modules or CSS:", err);
      }
    };

    loadSwiper();
  }, []);

  const { Swiper, SwiperSlide, Navigation } = swiperComponents;

  if (!Swiper || !SwiperSlide || !isCssLoaded) {
    return <LoadingIndicator fullPage={true} />;
  }

  if (
    content.fieldTopicsClickSuggestions &&
    content.fieldTopicsClickSuggestions.length >= 1
  ) {
    return (
      <>
        {content.fieldHeading && (
          <div className="container headline-container">
            <h3 className="headline headline-s">{content.fieldHeading}</h3>
          </div>
        )}

        <div className={"container position-relative"}>
          <SwiperSimpleNavigation prevRef={prevRef} nextRef={nextRef} />
          <Swiper
            spaceBetween={16}
            slidesPerView={4}
            modules={[Navigation]}
            breakpoints={{
              0: {
                slidesPerView: simpleDesign ? 1 : 1.1,
                centeredSlides: false,
              },
              768: {
                slidesPerView: simpleDesign ? 2 : 2.1,
                centeredSlides: false,
              },
              1024: {
                slidesPerView:
                  content.fieldTopicsClickSuggestions.length >= 4
                    ? 4
                    : simpleDesign
                    ? 2
                    : 2.8,
                centeredSlides: false,
              },
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              // Assign navigation refs before Swiper initializes
              if (swiper.params.navigation) {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }
            }}
          >
            {content.fieldTopicsClickSuggestions &&
              content.fieldTopicsClickSuggestions.map((link, index) => {
                let linkOrFilter = {
                  title:
                    (link.fieldUseFilterLink && link.fieldFilterLinkLabel) ||
                    link.fieldLink.title,
                  uri: {
                    path:
                      (link.fieldUseFilterLink && link.fieldFilterLink) ||
                      link.fieldLink?.uri?.path,
                  },
                };

                if (linkOrFilter?.title && linkOrFilter?.uri?.path) {
                  return (
                    <SwiperSlide key={index}>
                      {({ isActive }) => (
                        <div key={index} data-is-active={isActive}>
                          <Link
                            link={linkOrFilter}
                            forceSameTab={!!link.fieldFilterLink}
                            aria-label={`Link: ${linkOrFilter.title}`}
                          >
                            <TopicClickSuggestion
                              content={link}
                              linkOrFilter={linkOrFilter}
                            />
                          </Link>
                        </div>
                      )}
                    </SwiperSlide>
                  );
                }
              })}
          </Swiper>
        </div>
      </>
    );
  }
};

ClickSuggestionsTopicsRbxCompany.propTypes = {
  viewMode: PropTypes.string,
  content: PropTypes.shape({
    fieldTopicsClickSuggestions: PropTypes.arrayOf(
      PropTypes.shape({
        fieldImage: PropTypes.object,
        fieldLink: PropTypes.shape({
          title: PropTypes.string,
          uri: PropTypes.shape({
            routed: PropTypes.bool,
            path: PropTypes.string,
          }),
        }),
      })
    ),
  }),
};

export default ClickSuggestionsTopicsRbxCompany;
